import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux';
import toast from 'react-hot-toast';
import { CustomSwitch } from 'components/common/Switch';
import { SubmitButton } from 'components/common/buttons/SubmitButton';
import ErrorMessage from 'components/common/ErrorMessage';
import { setError } from 'slices/typeSlice';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import moment from 'moment';
import { Select } from 'components/common/Select';
import { languageTierSelector } from 'slices/languageTierSlice';
import { fetchTemplateLanguagesTiers } from 'slices/languageTierSlice';
import { useNavigate } from 'react-router-dom';
import { updateTemplate } from 'slices/templateSlice';
import { addTemplate } from 'slices/templateSlice';
import FullPageLoader from 'components/common/FullPageLoader';
import { usePermissions } from 'layouts/Admin';
import TranslationTiers from 'components/template/TranslationTiers';
import { useTranslation } from 'react-i18next';

export const filterUnit = [
  { label: "Hour", value: "hour" },
  { label: "Minute", value: "minute" }
]
export const filterIncremental = [
  { label: "Minute", value: "minute" },
  { label: "30 Minute", value: "30_minute" },
  { label: "1 Hour", value: "1_hour" },
]
export const filterType = [
  { label: "Interpretation", value: "interpretation" },
  { label: "Translation", value: "translation" },
]

export const TimeInput = ({ name, rule, control, onChange }) => (
  <Controller name={name} control={control} rules={{ required: rule }} render={({ field, fieldState: { error } }) => (
    <div style={{ width: "100%" }}>
      <div className="input-image">
        <input type="time" name={name} required placeholder="hh:mm" {...field} value={field.value} onChange={(e) => { field.onChange(e.target.value); onChange && onChange(e.target.value); }} />
        <img src="/images/clock.png" alt="Clock Icon" className="custom-icon" />
      </div>
      {error?.message && <div style={{ marginTop: 10 }}><ErrorMessage text={error?.message} /></div>}
    </div>
  )} />
);

export const RateInput = ({ name, control, placeholder, currency_symbol = false }) => (
  <div className="template rate-input-wrapper">
    <Controller name={name} control={control} rules={{ required: "This field is required" }} render={({ field, fieldState: { error } }) => (
      <>
        <div className={`${currency_symbol ? 'input-dollar-group' : ''}`}>
          {currency_symbol && <span className="dollar-prefix">$</span>}
          <input className={`input-in type_inp ${currency_symbol ? 'dollar-padding' : ''}`} type="number" name={name} placeholder={placeholder} {...field} onChange={(e) => { let value = e.target.value; if (value === "") { field.onChange(""); return; } value = parseFloat(value); field.onChange(value < 0 ? 0 : value); }} />
        </div>
        {error?.message && <div style={{ marginTop: 10 }}><ErrorMessage text={error?.message} /></div>}
      </>
    )} />
  </div>
);

export const UnitSelect = ({ name, control }) => {
  const { t } = useTranslation();
  const options = filterUnit.map((option) => ({ ...option, label: option.value === "hour" ? t("appointmentForm.hour") : t("appointmentForm.minute") }));
  return <Controller name={name} control={control} rules={{ required: "This field is required" }} defaultValue={filterUnit[0].value} render={({ field, fieldState: { error } }) => (
    <div className='template select_box' style={{ width: "100%" }}>
      <Select name={name} options={options} value={options.find((option) => option.value === field.value)} isClearable={false} disabledSearch={true} onChange={(value) => field.onChange(value)} />
      {error?.message && <div style={{ marginTop: 10 }}><ErrorMessage text={error.message} /></div>}
    </div>
  )} />;
}
const TemplateTimes = ({ control, watch, setValue, languageTiers }) => {
  const { t } = useTranslation();
  const incrementalOptions = filterIncremental.map((option) => ({ ...option, label: option.value === "minute" ? t("appointmentForm.minute") : option.value === "30_minute" ? t("templateForm.thirtyMinute") : t("templateForm.oneHour") }));
  const typeOptions = filterType.map((option) => ({ ...option, label: option.value === "interpretation" ? t("templateForm.interpretation") : t("templateForm.translation") }));


  const handleNormalHoursChange = () => {
    const normalHourStartTime = watch("normal_hour_start_time");
    const normalHourEndTime = watch("normal_hour_end_time");

    if (normalHourStartTime && normalHourEndTime) {
      const normalStart = moment(normalHourStartTime, "HH:mm");
      const normalEnd = moment(normalHourEndTime, "HH:mm");

      const afterStart = moment(normalEnd).add(1, "minutes");
      const afterEnd = moment(normalStart).subtract(1, "minutes").add(1, "day");

      setValue("after_hour_start_time", afterStart.format("HH:mm"));
      setValue("after_hour_end_time", afterEnd.format("HH:mm"));
    }
  };

  const handleAfterHoursChange = () => {
    const afterHourStartTime = watch("after_hour_start_time");
    const afterHourEndTime = watch("after_hour_end_time");

    if (afterHourStartTime && afterHourEndTime) {
      const afterStart = moment(afterHourStartTime, "HH:mm");
      const afterEnd = moment(afterHourEndTime, "HH:mm");

      const normalStart = moment(afterEnd).add(1, "minutes").subtract(1, "day");
      const normalEnd = moment(afterStart).subtract(1, "minutes");

      setValue("normal_hour_start_time", normalStart.format("HH:mm"));
      setValue("normal_hour_end_time", normalEnd.format("HH:mm"));
    }
  };

  const handleChangeMinDuration = (value) => {
    if (value == "minute" || value == "30_minute") {
      languageTiers.forEach((element, index) => {
        setValue(`interpretationRates.opi_normal_mins_time_unit.${index}`, 'minute')
        setValue(`interpretationRates.vri_normal_mins_time_unit.${index}`, 'minute')
        setValue(`interpretationRates.inperson_normal_mins_time_unit.${index}`, 'minute')
        setValue(`interpretationRates.opi_after_mins_time_unit.${index}`, "minute");
        setValue(`interpretationRates.vri_after_mins_time_unit.${index}`, "minute");
        setValue(`interpretationRates.inperson_after_mins_time_unit.${index}`, "minute");
      });
    } else {
      languageTiers.forEach((element, index) => {
        setValue(`interpretationRates.opi_normal_mins_time_unit.${index}`, 'hour')
        setValue(`interpretationRates.vri_normal_mins_time_unit.${index}`, 'hour')
        setValue(`interpretationRates.inperson_normal_mins_time_unit.${index}`, 'hour')
        setValue(`interpretationRates.opi_after_mins_time_unit.${index}`, "hour");
        setValue(`interpretationRates.vri_after_mins_time_unit.${index}`, "hour");
        setValue(`interpretationRates.inperson_after_mins_time_unit.${index}`, "hour");
      });
    }
  }

  const selectedType = watch("type");

  return (
    <>
      <div className="row-primary">
        <h2>{t("templateForm.name")}</h2>
        <div className='w-100'>
          <Controller
            name="name"
            rules={{ required: t("templateForm.nameRequired") }}
            control={control}
            render={({ field, fieldState: { error: fieldError } }) => (
              <>
                <input className="input-in type_inp" type="text" name='name' placeholder={t("templateForm.templateNamePlaceholder")} {...field} />
                {fieldError?.message && <div style={{ marginTop: 10 }}><ErrorMessage text={fieldError?.message} /></div>}
              </>
            )}
          />
        </div>
      </div>


      {selectedType === "interpretation" && (
      <>
      {/* Normal Hours */}
      <div className="row-primary m-0 row-r justify-content-between flex-row">
        <h2 className='fst-italic fw-bold mt-0'>{t("templateForm.normalHours")}</h2>
      </div>
      <div className="template foam_page_row">
        <div className="row-primary">
          <h2>{t("appointmentForm.startTime")}</h2>
          <TimeInput
            control={control}
            name="normal_hour_start_time"
            rule={t("appointmentForm.selectStartTime")}
            onChange={handleNormalHoursChange}
          />
        </div>

        <div className="row-primary">
          <h2>{t("appointmentForm.endTime")}</h2>
          <TimeInput
            control={control}
            name="normal_hour_end_time"
            rule={t("appointmentForm.selectEndTime")}
            onChange={handleNormalHoursChange}
          />
        </div>
      </div>

      {/* After Hours */}
      <div className="row-primary m-0 row-r justify-content-between flex-row">
        <h2 className='fst-italic fw-bold mt-0'>{t("templateForm.afterHours")}</h2>
      </div>
      <div className="template foam_page_row">
        <div className="row-primary">
          <h2>{t("appointmentForm.startTime")}</h2>
          <TimeInput
            control={control}
            name="after_hour_start_time"
            rule={t("appointmentForm.selectStartTime")}
            onChange={handleAfterHoursChange}
          />
        </div>

        <div className="row-primary">
          <h2>{t("appointmentForm.endTime")}</h2>
          <TimeInput
            control={control}
            name="after_hour_end_time"
            rule={t("appointmentForm.selectEndTime")}
            onChange={handleAfterHoursChange}
          />
        </div>
      </div>

      {/* Grace Period */}
      <div className='template foam_page_row mt-3'>
        <div className="row-primary">
          <h2>{t("templateForm.gracePeriod")}</h2>
          <div style={{ width: "100%" }}>
            <Controller
              name="grace_period"
              control={control}
              rules={{ required: t("templateForm.gracePeriodRequired") }}
              render={({ field, fieldState: { error: fieldError } }) => (
                <>
                  <input className="input-in type_inp" type="number" name='grace_period' placeholder={t("templateForm.gracePeriodPlaceholder")} {...field}
                    onChange={
                      (e) => {
                        field.onChange(parseFloat(e.target.value));
                        if (e.target.value < 0) {
                          field.onChange(0);
                        }
                      }
                    }
                  />
                  {fieldError?.message &&
                    <div style={{ marginBottom: 10 }}>
                      <ErrorMessage text={fieldError?.message} />
                    </div>
                  }
                </>
              )}
            />
          </div>
        </div>

        <div className="row-primary">
          <h2>{t("templateForm.opiVriGracePeriod")}</h2>
          <div style={{ width: "100%" }}>
            <Controller
              name="opi_vri_grace_period"
              control={control}
              rules={{ required: t("templateForm.opiVriGracePeriodRequired") }}
              render={({ field, fieldState: { error: fieldError } }) => (
                <>
                  <input className="input-in type_inp" type="number" name='opi_vri_grace_period' placeholder={t("templateForm.gracePeriodPlaceholder")} {...field}
                    onChange={
                      (e) => {
                        field.onChange(parseFloat(e.target.value));
                        if (e.target.value < 0) {
                          field.onChange(0);
                        }
                      }
                    }
                  />
                  {fieldError?.message &&
                    <div style={{ marginBottom: 10 }}>
                      <ErrorMessage text={fieldError?.message} />
                    </div>
                  }
                </>
              )}
            />
          </div>
        </div>
      </div>

      <div className='template foam_page_row mt-3'>
        <div className="row-primary">
          <h2>{t("templateForm.incremental")}</h2>
          <Controller
            name="incremental"
            control={control}
            rules={{ required: t("templateForm.incrementalRequired") }}
            render={({ field, fieldState: { error: fieldError } }) =>
              <div className='template select_box' style={{ width: "100%" }}>
                <Select
                  name="incremental"
                  options={incrementalOptions}
                  value={field.value ? incrementalOptions.find((opt) => field.value == opt.value) : ""}
                  isClearable={false}
                  disabledSearch={true}
                  onChange={(value) => {
                    field.onChange(value);
                    handleChangeMinDuration(value);
                  }}
                />
                {fieldError?.message && (
                  <div style={{ marginTop: 10 }}>
                    <ErrorMessage text={fieldError?.message} />
                  </div>
                )}
              </div>
            }
          />
        </div>
      </div>
      </>
      )}

      {/* Rush Fee  */}
      <div className='template foam_page_row mt-3'>
        <div className="row-primary">
          <h2>{t("templateForm.rushFee")}</h2>
          <div style={{ width: "100%" }}>
            <Controller
              name="rush_fee"
              control={control}
              rules={{ required: t("templateForm.rushFeeRequired") }}
              render={({ field, fieldState: { error: fieldError } }) => (
                <>
                  <input className="input-in type_inp" type="number" name='rush_fee' placeholder={t("templateForm.rushFeePlaceholder")} {...field}
                    onChange={
                      (e) => {
                        field.onChange(parseFloat(e.target.value));
                        if (e.target.value < 0) {
                          field.onChange(0);
                        }
                      }
                    }
                  />
                  {fieldError?.message &&
                    <div style={{ marginBottom: 10 }}>
                      <ErrorMessage text={fieldError?.message} />
                    </div>
                  }
                </>
              )}
            />
          </div>
        </div>

        <div className='row-primary'>
          <h2>{t("templateForm.type")}</h2>
          <Controller
            name="type"
            control={control}
            rules={{ required: t("templateForm.typeRequired") }}
            render={({ field, fieldState: { error: fieldError } }) =>
              <div className='template select_box' style={{ width: "100%" }}>
                <Select
                  name="type"
                  options={typeOptions}
                  value={field.value ? typeOptions.find((opt) => field.value == opt.value) : ""}
                  isClearable={false}
                  disabledSearch={true}
                  onChange={(value) => {
                    field.onChange(value);
                    if (value === 'interpretation') {
                      languageTiers?.forEach((element, index) => {
                        setValue(`interpretationRates.opi_normal_rate_time_unit.${index}`, filterUnit[0].value);
                        setValue(`interpretationRates.vri_normal_rate_time_unit.${index}`, filterUnit[0].value);
                        setValue(`interpretationRates.inperson_normal_time_unit.${index}`, filterUnit[0].value);
                        setValue(`interpretationRates.opi_after_rate_time_unit.${index}`, filterUnit[0].value);
                        setValue(`interpretationRates.vri_after_rate_time_unit.${index}`, filterUnit[0].value);
                        setValue(`interpretationRates.inperson_after_time_unit.${index}`, filterUnit[0].value);

                        setValue(`interpretationRates.opi_normal_mins_time_unit.${index}`, filterIncremental[0].value);
                        setValue(`interpretationRates.vri_normal_mins_time_unit.${index}`, filterIncremental[0].value);
                        setValue(`interpretationRates.inperson_normal_mins_time_unit.${index}`, filterIncremental[0].value);
                        setValue(`interpretationRates.opi_after_mins_time_unit.${index}`, filterIncremental[0].value);
                        setValue(`interpretationRates.vri_after_mins_time_unit.${index}`, filterIncremental[0].value);
                        setValue(`interpretationRates.inperson_after_mins_time_unit.${index}`, filterIncremental[0].value);
                      });
                    }
                  }}
                />
                {fieldError?.message && (
                  <div style={{ marginTop: 10 }}>
                    <ErrorMessage text={fieldError?.message} />
                  </div>
                )}
              </div>
            }
          />
        </div>
      </div>
      
    </>
  );
};
const LanguageTiers = ({ languageTiers, control, errors, item }) => {
  const { t } = useTranslation();

  return (
    <>
      {languageTiers && languageTiers?.map((val, index) => {
        return (<>
          <div className="row-primary mt-3 mb-5 justify-content-center">
            <h2 className='fw-bold text-center text-uppercase'>{t("templateForm.interpretationRates")}</h2>
          </div>
          <div key={index} className='row-primary rate_container_outer'>
            <h2 className='fw-bold text-uppercase'>{val?.name || val?.tiers[0]?.name || 'N/A'}</h2>
            <div className='rate_container_wrapper'>
              <h2 className='text-center my-3 fw-bold fst-italic'>{t("templateForm.normalHoursRequired")}</h2>
              <div className='template_create rate_container'>
                <div className="row-primary align-items-end inner">
                  <h2 className='mb-3 my-0'>{t("templateForm.rates")}</h2>
                  <div className='Inter_rate_row'>
                    <h4>{t("templateForm.opi")}</h4>
                    <div className='create inter_wrapper'>
                      <RateInput control={control} name={`interpretationRates.opi_normal_rate.${index}`} placeholder={t("templateForm.opiRate")} currency_symbol={true} />
                      <UnitSelect control={control} name={`interpretationRates.opi_normal_rate_time_unit.${index}`} />
                    </div>
                  </div>
                  <div className='Inter_rate_row'>
                    <h4>{t("templateForm.vri")}</h4>
                    <div className='create inter_wrapper'>
                      <RateInput control={control} name={`interpretationRates.vri_normal_rate.${index}`} placeholder={t("templateForm.vriRate")} currency_symbol={true} />
                      <UnitSelect control={control} name={`interpretationRates.vri_normal_rate_time_unit.${index}`} />
                    </div>
                  </div>
                  <div className='Inter_rate_row'>
                    <h4>{t("appointmentForm.inPerson")}</h4>
                    <div className='create inter_wrapper'>
                      <RateInput control={control} name={`interpretationRates.inperson_normal_rate.${index}`} placeholder={t("templateForm.inPersonRate")} currency_symbol={true} />
                      <UnitSelect control={control} name={`interpretationRates.inperson_normal_time_unit.${index}`} />
                    </div>
                  </div>
                </div>

                <div className="row-primary align-items-end inner">
                  <h2 className='mb-3 my-0'>{t("templateForm.minimumMinutes")}</h2>
                  <div className='Inter_rate_row'>
                    <div className='create inter_wrapper'>
                      <RateInput control={control} name={`interpretationRates.opi_normal_mins.${index}`} placeholder={t("templateForm.opiMinimumMinutes")} />
                      <UnitSelect control={control} name={`interpretationRates.opi_normal_mins_time_unit.${index}`} />
                    </div>
                  </div>
                  <div className='Inter_rate_row'>
                    <div className='create inter_wrapper'>
                      <RateInput control={control} name={`interpretationRates.vri_normal_mins.${index}`} placeholder={t("templateForm.vriMinimumMinutes")} />
                      <UnitSelect control={control} name={`interpretationRates.vri_normal_mins_time_unit.${index}`} />
                    </div>
                  </div>
                  <div className='Inter_rate_row'>
                    <div className='create inter_wrapper'>
                      <RateInput control={control} name={`interpretationRates.inperson_normal_mins.${index}`} placeholder={t("templateForm.inPersonMinimumMinutes")} />
                      <UnitSelect control={control} name={`interpretationRates.inperson_normal_mins_time_unit.${index}`} />
                    </div>
                  </div>
                </div>
              </div>
              <h2 className='text-center my-3 fw-bold fst-italic'>{t("templateForm.afterHoursRequired")}</h2>
              <div className='template_create rate_container'>
                <div className="row-primary align-items-end inner">
                  <h2 className='mb-3 my-0'>{t("templateForm.rates")}</h2>
                  <div className='Inter_rate_row'>
                    <h4>{t("templateForm.opi")}</h4>
                    <div className='create inter_wrapper'>
                      <RateInput control={control} name={`interpretationRates.opi_after_rate.${index}`} placeholder={t("templateForm.opiRate")} currency_symbol={true} />
                      <UnitSelect control={control} name={`interpretationRates.opi_after_rate_time_unit.${index}`} />
                    </div>
                  </div>

                  <div className='Inter_rate_row'>
                    <h4>{t("templateForm.vri")}</h4>
                    <div className='create inter_wrapper'>
                      <RateInput control={control} name={`interpretationRates.vri_after_rate.${index}`} placeholder={t("templateForm.vriRate")} currency_symbol={true} />
                      <UnitSelect control={control} name={`interpretationRates.vri_after_rate_time_unit.${index}`} />
                    </div>
                  </div>

                  <div className='Inter_rate_row'>
                    <h4>{t("appointmentForm.inPerson")}</h4>
                    <div className='create inter_wrapper'>
                      <RateInput control={control} name={`interpretationRates.inperson_after_rate.${index}`} placeholder={t("templateForm.inPersonRate")} currency_symbol={true} />
                      <UnitSelect control={control} name={`interpretationRates.inperson_after_time_unit.${index}`} />
                    </div>
                  </div>
                </div>

                <div className="row-primary align-items-end inner">
                  <h2 className='mb-3 my-0'>{t("templateForm.minimumMinutes")}</h2>
                  <div className='Inter_rate_row'>
                    <div className='create inter_wrapper'>
                      <RateInput control={control} name={`interpretationRates.opi_after_mins.${index}`} placeholder={t("templateForm.opiMinimumMinutes")} />
                      <UnitSelect control={control} name={`interpretationRates.opi_after_mins_time_unit.${index}`} />
                    </div>
                  </div>

                  <div className='Inter_rate_row'>
                    <div className='create inter_wrapper'>
                      <RateInput control={control} name={`interpretationRates.vri_after_mins.${index}`} placeholder={t("templateForm.vriMinimumMinutes")} />
                      <UnitSelect control={control} name={`interpretationRates.vri_after_mins_time_unit.${index}`} />
                    </div>
                  </div>

                  <div className='Inter_rate_row'>
                    <div className='create inter_wrapper'>
                      <RateInput control={control} name={`interpretationRates.inperson_after_mins.${index}`} placeholder={t("templateForm.inPersonMinimumMinutes")} />
                      <UnitSelect control={control} name={`interpretationRates.inperson_after_mins_time_unit.${index}`} />
                    </div>
                  </div>
                </div>
              </div>
              <br />
              <br />
              <br />
            </div>
          </div>
        </>)
      })}
    </>
  )
}
const TemplateForm = ({ item, loader, error }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { checkPermission } = usePermissions()
  const { templanguageTiers: languageTiers } = useSelector(languageTierSelector);
  const [tierId, setTierId] = useState([]);

  const interpretationRates =
  {
    tier_ids: languageTiers?.map((val) => val?.id) || [],
    opi_normal_rate: [],
    vri_normal_rate: [],
    inperson_normal_rate: [],
    opi_normal_rate_time_unit: [],
    vri_normal_rate_time_unit: [],
    inperson_normal_time_unit: [],
    opi_normal_mins: [],
    vri_normal_mins: [],
    inperson_normal_mins: [],
    opi_normal_mins_time_unit: [],
    vri_normal_mins_time_unit: [],
    inperson_normal_mins_time_unit: [],
    opi_after_rate: [],
    vri_after_rate: [],
    inperson_after_rate: [],
    opi_after_rate_time_unit: [],
    vri_after_rate_time_unit: [],
    inperson_after_time_unit: [],
    opi_after_mins: [],
    vri_after_mins: [],
    inperson_after_mins: [],
    opi_after_mins_time_unit: [],
    vri_after_mins_time_unit: [],
    inperson_after_mins_time_unit: [],
  }

  const translationRates = {
    tier_ids: languageTiers?.map((val) => val?.id) || [],
    translation_rate: [],
    editing_rate: [],
    dtp_rate: [],
    review_rate: [],
    audio_recording_rate: [],
    transcription_rate: [],
  }

  const methods = useForm({
    defaultValues: {
      type: filterType[0].value,
      interpretationRates,
      translationRates,
    }
  });

  const { register, handleSubmit, watch, formState: { errors }, control, setValue, clearErrors, reset } = methods;
  const [status, setStatus] = useState(1);

  useEffect(() => {
    if (item?.id) return;
    reset({
      type: filterType[0].value,
      incremental: filterIncremental[0].value,
      interpretationRates: {
        tier_ids: languageTiers?.map((val) => val?.id) || [],
        opi_normal_rate: [],
        vri_normal_rate: [],
        inperson_normal_rate: [],
        opi_normal_rate_time_unit: languageTiers?.map(l => filterUnit[0].value) || [],
        vri_normal_rate_time_unit: languageTiers?.map(l => filterUnit[0].value) || [],
        inperson_normal_time_unit: languageTiers?.map(l => filterUnit[0].value) || [],
        opi_normal_mins: [],
        vri_normal_mins: [],
        inperson_normal_mins: [],
        opi_normal_mins_time_unit: languageTiers?.map(l => filterIncremental[0].value) || [],
        vri_normal_mins_time_unit: languageTiers?.map(l => filterIncremental[0].value) || [],
        inperson_normal_mins_time_unit: languageTiers?.map(l => filterIncremental[0].value) || [],
        opi_after_rate: [],
        vri_after_rate: [],
        inperson_after_rate: [],
        opi_after_rate_time_unit: languageTiers?.map(l => filterUnit[0].value) || [],
        vri_after_rate_time_unit: languageTiers?.map(l => filterUnit[0].value) || [],
        inperson_after_time_unit: languageTiers?.map(l => filterUnit[0].value) || [],
        opi_after_mins: [],
        vri_after_mins: [],
        inperson_after_mins: [],
        opi_after_mins_time_unit: languageTiers?.map(l => filterIncremental[0].value) || [],
        vri_after_mins_time_unit: languageTiers?.map(l => filterIncremental[0].value) || [],
        inperson_after_mins_time_unit: languageTiers?.map(l => filterIncremental[0].value) || [],
      },
      translationRates: {
        tier_ids: languageTiers?.map((val) => val?.id) || [],
        translation_rate: [],
        editing_rate: [],
        dtp_rate: [],
        review_rate: [],
        audio_recording_rate: [],
        transcription_rate: [],
      }
    })

  }, [languageTiers, reset, item])

  useEffect(() => {
    if (item) {
      const ids = item?.interpretation_rates?.map(val => val.tier_id) || [];
      setTierId(ids);

      dispatch(fetchTemplateLanguagesTiers(1, { ids }));
    } else {
      dispatch(fetchTemplateLanguagesTiers());
    }
  }, [item]);


  const resetAllFields = () => {
    setValue('name', '')
    setValue('type', filterType[0].value)
    setValue('normal_hour_start_time', '')
    setValue('normal_hour_end_time', '')
    setValue('after_hour_start_time', '')
    setValue('after_hour_end_time', '')
    setValue('grace_period', '')
    setValue('opi_vri_grace_period', '')
    setValue('incremental', 'minute')
    setValue('rush_fee', '')
    setStatus(1)

    setValue('interpretationRates.opi_normal_rate', languageTiers?.map(() => '') || [])
    setValue('interpretationRates.vri_normal_rate', languageTiers?.map(() => '') || [])
    setValue('interpretationRates.inperson_normal_rate', languageTiers?.map(() => '') || [])
    setValue('interpretationRates.opi_normal_rate_time_unit', languageTiers?.map(() => filterUnit[0].value) || [])
    setValue('interpretationRates.vri_normal_rate_time_unit', languageTiers?.map(() => filterUnit[0].value) || [])
    setValue('interpretationRates.inperson_normal_time_unit', languageTiers?.map(() => filterUnit[0].value) || [])
    setValue('interpretationRates.opi_normal_mins', languageTiers?.map(() => '') || [])
    setValue('interpretationRates.vri_normal_mins', languageTiers?.map(() => '') || [])
    setValue('interpretationRates.inperson_normal_mins', languageTiers?.map(() => '') || [])
    setValue('interpretationRates.opi_normal_mins_time_unit', languageTiers?.map(() => filterIncremental[0].value) || [])
    setValue('interpretationRates.vri_normal_mins_time_unit', languageTiers?.map(() => filterIncremental[0].value) || [])
    setValue('interpretationRates.inperson_normal_mins_time_unit', languageTiers?.map(() => filterIncremental[0].value) || [])

    setValue('interpretationRates.opi_after_rate', languageTiers?.map(() => '') || [])
    setValue('interpretationRates.vri_after_rate', languageTiers?.map(() => '') || [])
    setValue('interpretationRates.inperson_after_rate', languageTiers?.map(() => '') || [])
    setValue('interpretationRates.opi_after_rate_time_unit', languageTiers?.map(() => filterUnit[0].value) || [])
    setValue('interpretationRates.vri_after_rate_time_unit', languageTiers?.map(() => filterUnit[0].value) || [])
    setValue('interpretationRates.inperson_after_time_unit', languageTiers?.map(() => filterUnit[0].value) || [])
    setValue('interpretationRates.opi_after_mins', languageTiers?.map(() => '') || [])
    setValue('interpretationRates.vri_after_mins', languageTiers?.map(() => '') || [])
    setValue('interpretationRates.inperson_after_mins', languageTiers?.map(() => '') || [])
    setValue('interpretationRates.opi_after_mins_time_unit', languageTiers?.map(() => filterIncremental[0].value) || [])
    setValue('interpretationRates.vri_after_mins_time_unit', languageTiers?.map(() => filterIncremental[0].value) || [])
    setValue('interpretationRates.inperson_after_mins_time_unit', languageTiers?.map(() => filterIncremental[0].value) || [])

    setValue('translationRates.tier_ids', languageTiers?.map((val) => val?.id) || [])
    setValue('translationRates.translation_rate', languageTiers?.map(() => '') || [])
    setValue('translationRates.editing_rate', languageTiers?.map(() => '') || [])
    setValue('translationRates.dtp_rate', languageTiers?.map(() => '') || [])
    setValue('translationRates.review_rate', languageTiers?.map(() => '') || [])
    setValue('translationRates.audio_recording_rate', languageTiers?.map(() => '') || [])
    setValue('translationRates.transcription_rate', languageTiers?.map(() => '') || [])
  }

  useEffect(() => {
    if (!item) return;

    setValue('name', item?.name || '');
    setValue('type', item?.type || filterType[0].value);
    setValue('normal_hour_start_time', item?.normal_hour_start_time ? moment(item.normal_hour_start_time, "HH:mm:ss").format("HH:mm") : "" || '');
    setValue('normal_hour_end_time', item?.normal_hour_end_time ? moment(item.normal_hour_end_time, "HH:mm:ss").format("HH:mm") : "" || '');
    setValue('after_hour_start_time', item?.after_hour_start_time ? moment(item.after_hour_start_time, "HH:mm:ss").format("HH:mm") : "" || '');
    setValue('after_hour_end_time', item?.after_hour_end_time ? moment(item.after_hour_end_time, "HH:mm:ss").format("HH:mm") : "" || '');
    setValue('grace_period', item?.grace_period || '');
    setValue('opi_vri_grace_period', item?.opi_vri_grace_period ?? (item?.grace_period || ''));
    setValue('incremental', item?.incremental || 'minute');
    setValue('rush_fee', item?.rush_fee || '');
    setStatus((typeof (item?.status) == "number") ? item?.status : 1);

    if (item?.interpretation_rates) {
      setValue('interpretationRates.opi_normal_rate', item.interpretation_rates.map(r => r.opi_normal_rate));
      setValue('interpretationRates.vri_normal_rate', item.interpretation_rates.map(r => r.vri_normal_rate));
      setValue('interpretationRates.inperson_normal_rate', item.interpretation_rates.map(r => r.inperson_normal_rate));
      setValue('interpretationRates.opi_normal_rate_time_unit', item.interpretation_rates.map(r => r.opi_normal_rate_time_unit));
      setValue('interpretationRates.vri_normal_rate_time_unit', item.interpretation_rates.map(r => r.vri_normal_rate_time_unit));
      setValue('interpretationRates.inperson_normal_time_unit', item.interpretation_rates.map(r => r.inperson_normal_time_unit));

      setValue('interpretationRates.opi_normal_mins', item.interpretation_rates.map(r => r.opi_normal_mins));
      setValue('interpretationRates.vri_normal_mins', item.interpretation_rates.map(r => r.vri_normal_mins));
      setValue('interpretationRates.inperson_normal_mins', item.interpretation_rates.map(r => r.inperson_normal_mins));
      setValue('interpretationRates.opi_normal_mins_time_unit', item.interpretation_rates.map(r => r.opi_normal_mins_time_unit));
      setValue('interpretationRates.vri_normal_mins_time_unit', item.interpretation_rates.map(r => r.vri_normal_mins_time_unit));
      setValue('interpretationRates.inperson_normal_mins_time_unit', item.interpretation_rates.map(r => r.inperson_normal_mins_time_unit));

      setValue('interpretationRates.opi_after_rate', item.interpretation_rates.map(r => r.opi_after_rate));
      setValue('interpretationRates.vri_after_rate', item.interpretation_rates.map(r => r.vri_after_rate));
      setValue('interpretationRates.inperson_after_rate', item.interpretation_rates.map(r => r.inperson_after_rate));
      setValue('interpretationRates.opi_after_rate_time_unit', item.interpretation_rates.map(r => r.opi_after_rate_time_unit));
      setValue('interpretationRates.vri_after_rate_time_unit', item.interpretation_rates.map(r => r.vri_after_rate_time_unit));
      setValue('interpretationRates.inperson_after_time_unit', item.interpretation_rates.map(r => r.inperson_after_time_unit));

      setValue('interpretationRates.opi_after_mins', item.interpretation_rates.map(r => r.opi_after_mins));
      setValue('interpretationRates.vri_after_mins', item.interpretation_rates.map(r => r.vri_after_mins));
      setValue('interpretationRates.inperson_after_mins', item.interpretation_rates.map(r => r.inperson_after_mins));
      setValue('interpretationRates.opi_after_mins_time_unit', item.interpretation_rates.map(r => r.opi_after_mins_time_unit));
      setValue('interpretationRates.vri_after_mins_time_unit', item.interpretation_rates.map(r => r.vri_after_mins_time_unit));
      setValue('interpretationRates.inperson_after_mins_time_unit', item.interpretation_rates.map(r => r.inperson_after_mins_time_unit));
    }

    if (item?.translation_rates) {
      setValue('translationRates.tier_ids', item.translation_rates.map(r => r.tier_id));
      setValue('translationRates.translation_rate', item.translation_rates.map(r => r.translation_rate));
      setValue('translationRates.editing_rate', item.translation_rates.map(r => r.editing_rate));
      setValue('translationRates.dtp_rate', item.translation_rates.map(r => r.dtp_rate));
      setValue('translationRates.review_rate', item.translation_rates.map(r => r.review_rate));
      setValue('translationRates.audio_recording_rate', item.translation_rates.map(r => r.audio_recording_rate));
      setValue('translationRates.transcription_rate', item.translation_rates.map(r => r.transcription_rate));
    }

    dispatch(setError({}));

    return () => {
      // resetAllFields()
    }
  }, [item, languageTiers])

  const submitCallback = (status, message) => {
    if (status) {
      toast.success(message, {
        className: "successToast",
      })
      resetAllFields()
      navigate('/clients/templates')
    }
    else {
      console.log('onSubmit', error)
      toast.error(message,
        {
          className: "errorToast",
        }
      );
    }
  };

  const onSubmit = (data) => {
    dispatch(setError({}))
    const currentTierIds = languageTiers?.map((val) => val?.id) || [];
    const newData = {
      ...data,
      // ...data.interpretationRates,
      status,
    };
    // delete data.interpretationRates;

    if (data.type === 'translation') {
      Object.assign(newData, data.translationRates || {} , { tier_ids: currentTierIds });
    } else {
      Object.assign(newData, data.interpretationRates || {} , { tier_ids: currentTierIds });
    }

    delete newData.interpretationRates;
    delete newData.translationRates;

    if (item?.id) {
      dispatch(updateTemplate(newData, item?.id, submitCallback));
    } else {
      dispatch(addTemplate(newData, submitCallback));
    }
  }


  return (
    <>
      <FormProvider {...methods}>
        <form className='card_table p-4' onSubmit={handleSubmit(onSubmit)}>


          <TemplateTimes control={control} errors={errors} error={error} watch={watch} setValue={setValue} languageTiers={languageTiers} />

          {/* <LanguageTiers languageTiers={languageTiers} control={control} errors={errors} item={item} /> */}
          {watch('type') === 'translation' ? (
            <TranslationTiers languageTiers={languageTiers} control={control} errors={errors} item={item} />
          ) : (
            <LanguageTiers languageTiers={languageTiers} control={control} errors={errors} item={item} />
          )}

          {checkPermission('Status Client Template') &&
            <div className="row-primary align-items-center template_switch">
              <h2 className='mt-0'>{t("table.status")}</h2>
              <CustomSwitch
                labelText="active"
                checked={status == 1 ? true : false}
                handleChange={(checked) => setStatus(checked ? 1 : 0)}
              />
            </div>
          }

          <div className="Form-footer">
            <SubmitButton
              loader={loader}
              btnText={item?.id ? t("accountForm.update") : t("accountForm.create")}
            />
          </div>
        </form>
      </FormProvider>
      {loader
        &&
        <FullPageLoader />
      }
    </>
  )
}

export default TemplateForm
